import { countries } from "countries-list"
import { useMemo } from "react"
import { useQuery } from "@oysterhr/core"
import { EquityIssueRiskTiers, EquityIssuePlans } from "~/app/utils/constants"
import { EquityIssueHeatmapItem, Maybe } from "@oysterhr/schema"
import { gql } from "../gql"

export type EquityIssueTableColumns = (keyof typeof EquityIssuePlans)[]

export type UseEquityIssuesHeatmapProps = {
  countryCode?: string
  page?: number
  perPage?: number
  plans?: EquityIssueTableColumns
  riskTiers?: typeof EquityIssueRiskTiers
  supported?: boolean | null
  query?: string
}

export type EquityIssueHeatmapResponse = { equityIssuesHeatmap: EquityIssueHeatmapItem[] }

export type EquityIssueShape = {
  countryCode: string
  countryName: string
  geographicRegion: string
  sopRiskTier: number
  rsuRiskTier: number
  cshRiskTier: number
  supported: boolean
}

export type CountryIndex = keyof typeof countries

export const OYSTER_INTELLIGENCE_EQUITY_ISSUES_HEATMAP_QUERY = gql(`
  query OysterIntelligenceEquityIssuesHeatmapQuery(
    $countryCode: String
    $plans: [String!]
    $riskTiers: [Int!]
  ) {
    equityIssuesHeatmap(
      countryCode: $countryCode
      plans: $plans
      riskTiers: $riskTiers
    ) {
      countryCode
      sopRiskTier
      rsuRiskTier
      cshRiskTier
      supported
    }
  }
`)

export type EquityResult = Maybe<{
  countryName: string
  geographicRegion: string
  __typename?: "EquityIssueHeatmapItem"
  countryCode?: string
  sopRiskTier?: number
  rsuRiskTier?: number
  cshRiskTier?: number
  supported?: boolean
}>

export const useEquityIssuesHeatmap = ({ countryCode }: UseEquityIssuesHeatmapProps = {}) => {
  const variables = useMemo(() => {
    return {
      countryCode,
      plans: ["SOP", "RSU", "CSH"],
      riskTiers: EquityIssueRiskTiers,
    }
  }, [countryCode])

  const { data, loading, error } = useQuery(OYSTER_INTELLIGENCE_EQUITY_ISSUES_HEATMAP_QUERY, {
    variables,
  })

  const result: EquityResult = useMemo(() => {
    if (data?.equityIssuesHeatmap?.length) {
      const equityIssueForCountry = data.equityIssuesHeatmap.find(
        ({ countryCode: countryCodeFromData }) => countryCode === countryCodeFromData,
      )
      return {
        ...equityIssueForCountry,
        countryName: countries[equityIssueForCountry?.countryCode as CountryIndex]?.name,
        geographicRegion: countries[equityIssueForCountry?.countryCode as CountryIndex]?.continent,
      }
    }
    return null
  }, [data?.equityIssuesHeatmap, countryCode])

  return { result, loading, error }
}
